import { configureStore, combineReducers, type Action } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import { RESET_STORE } from './resetStore';
import storage from 'redux-persist/lib/storage/session';
import authReducer from './slices/authSlice';
import deviceReducer from './slices/deviceSlice';
import valveNodeReducer from './slices/valveNodeSlice';
import valveReducer from './slices/valveSlice';
import scheduleReducer from './slices/scheduleSlice';
import uiReducer from './slices/uiSlice';
import deviceAccessReducer from './slices/deviceAccessSlice';
import farmAccessReducer from './slices/farmAccessSlice';
import deviceSettingsReducer from './slices/deviceSettingsSlice';
import preferencesReducer from './slices/preferencesSlice';
import { farmReducer } from './slices/farmSlice';
import { fieldReducer } from './slices/farmSlice';
import { irrigationSectorReducer } from './slices/farmSlice';
import { pumpStationReducer } from './slices/farmSlice';
import { weatherStationReducer } from './slices/farmSlice';
import { geoImportReducer, irrigationZoneReducer, layoutEditorReducer, tableEditorReducer } from './slices/farmSlice';
import { cropSpeciesReducer, cropVarietyReducer } from './slices/cropsSlice';
import { waterBalanceReducer } from './slices/waterBalanceSlice';
import { irrigationSeasonReducer } from './slices/irrigationSeasonSlice';
import { tasksReducer } from './slices/tasksSlice';
import onboardingReducer from './slices/onboardingSlice';
import scheduleRecommendationsReducer from './slices/scheduleRecommendationsSlice';
import { farmMapReducer } from './slices/farmMapSlice';
import { adminReducer } from './slices/adminSlice';
import { programReducer } from './slices/programSlice';
import optaReducer from './slices/optaSlice';

// Create separate persist configs for auth, device access, and ui
const authPersistConfig = {
  key: 'auth',
  storage,
  blacklist: ['loading', 'error'], // Don't persist loading/error states
};

const deviceAccessPersistConfig = {
  key: 'deviceAccess',
  storage,
  blacklist: ['loading', 'error'], // Don't persist loading/error states
};

const farmAccessPersistConfig = {
  key: 'farmAccess',
  storage,
  blacklist: ['loading', 'error'], // Don't persist loading/error states
};

const uiPersistConfig = {
  key: 'ui',
  storage,
  whitelist: ['theme'], // Only persist theme preference
};

const preferencesPersistConfig = {
  key: 'preferences',
  storage,
  whitelist: ['preferences'], // Persist user preferences
};

// Only persist the weather cache so the dashboard doesn't re-fetch on every page reload.
// Other farm state (farms list, selectedFarm, etc.) is always loaded fresh.
const farmsPersistConfig = {
  key: 'farms',
  storage,
  whitelist: ['weatherData', 'weatherCachedAt'],
};

// Create persisted reducers
const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
const persistedDeviceAccessReducer = persistReducer(deviceAccessPersistConfig, deviceAccessReducer);
const persistedFarmAccessReducer = persistReducer(farmAccessPersistConfig, farmAccessReducer);
const persistedUiReducer = persistReducer(uiPersistConfig, uiReducer);
const persistedPreferencesReducer = persistReducer(preferencesPersistConfig, preferencesReducer);
const persistedFarmReducer = persistReducer(farmsPersistConfig, farmReducer);

const combinedReducer = combineReducers({
    auth: persistedAuthReducer,
    devices: deviceReducer,
    valveNodes: valveNodeReducer,
    valves: valveReducer,
    schedules: scheduleReducer,
    ui: persistedUiReducer,
    deviceAccess: persistedDeviceAccessReducer,
    farmAccess: persistedFarmAccessReducer,
    deviceSettings: deviceSettingsReducer,
    preferences: persistedPreferencesReducer,
    farms: persistedFarmReducer,
    fields: fieldReducer,
    irrigationSectors: irrigationSectorReducer,
    pumpStations: pumpStationReducer,
    weatherStations: weatherStationReducer,
    geoImport: geoImportReducer,
    irrigationZones: irrigationZoneReducer,
    layoutEditor: layoutEditorReducer,
    tableEditor: tableEditorReducer,
    cropSpecies: cropSpeciesReducer,
    cropVarieties: cropVarietyReducer,
    waterBalance: waterBalanceReducer,
    irrigationSeason: irrigationSeasonReducer,
    tasks: tasksReducer,
    onboarding: onboardingReducer,
    scheduleRecommendations: scheduleRecommendationsReducer,
    farmMap: farmMapReducer,
    admin: adminReducer,
    programs: programReducer,
    opta: optaReducer,
});

// Root reducer with a global reset. On RESET_STORE (dispatched at logout) every
// slice falls back to its initial state, so one user's in-memory data can never
// bleed into the next session on the same tab (the farm dropdown, device lists,
// water balance, etc. are NOT persisted, so they only leak via lingering memory
// when login happens without a full page reload). The persisted `ui` slice
// (theme) is preserved so logging out doesn't flip the user's theme.
const rootReducer = (
  state: ReturnType<typeof combinedReducer> | undefined,
  action: Action,
): ReturnType<typeof combinedReducer> => {
  if (action.type === RESET_STORE) {
    state = state ? ({ ui: state.ui } as ReturnType<typeof combinedReducer>) : undefined;
  }
  return combinedReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE', 'persist/REGISTER'],
      },
    }),
});

// Note: Authentication is now handled by AuthService, no need to initialize old manager

// Create and export persistor
export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Farm-related state selectors
export type FarmState = ReturnType<typeof store.getState>['farms'];
export type FarmAccessState = ReturnType<typeof store.getState>['farmAccess'];
export type FieldState = ReturnType<typeof store.getState>['fields'];
export type IrrigationSectorState = ReturnType<typeof store.getState>['irrigationSectors'];
export type PumpStationState = ReturnType<typeof store.getState>['pumpStations'];
export type WeatherStationState = ReturnType<typeof store.getState>['weatherStations'];
export type GeoImportState = ReturnType<typeof store.getState>['geoImport'];
export type IrrigationZoneState = ReturnType<typeof store.getState>['irrigationZones'];
export type LayoutEditorState = ReturnType<typeof store.getState>['layoutEditor'];
export type TableEditorState = ReturnType<typeof store.getState>['tableEditor'];
export type CropSpeciesState = ReturnType<typeof store.getState>['cropSpecies'];
export type CropVarietyState = ReturnType<typeof store.getState>['cropVarieties'];
export type PreferencesState = ReturnType<typeof store.getState>['preferences'];
export type WaterBalanceState = ReturnType<typeof store.getState>['waterBalance'];
export type IrrigationSeasonState = ReturnType<typeof store.getState>['irrigationSeason'];
export type TasksState = ReturnType<typeof store.getState>['tasks'];
export type OnboardingState = ReturnType<typeof store.getState>['onboarding'];
export type ScheduleRecommendationsState = ReturnType<typeof store.getState>['scheduleRecommendations'];
export type ProgramsState = ReturnType<typeof store.getState>['programs'];

// Persistor type for the persisted state
export type PersistedState = ReturnType<typeof store.getState>;